import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dropdown_black from "../../../../../../assets/AccountingModule/drop_down_icon.svg";
import color from "../../../../../../constants/color";
import styles from "./style";
import { CreditNoteValidation } from "../../../../../../helper/Validation";
import { accountOptions } from "../../../../../../constants/data";
dayjs.extend(utc);

const CreateNotesPage = ({ customerList, itemList, loading, createNotes }) => {
  const [customer, setCustomer] = useState(null);
  const [items, setItems] = useState([
    {
      name: "",
      itemId: "",
      account: "",
      quantity: "",
      rate: "",
      amount: "",
    },
  ]);

  const [customerId, setCustomerId] = useState("");
  const [subject, setSubject] = useState("");
  const [salesperson, setSalesperson] = useState("");
  const [customerNotes, setCustomerNotes] = useState("");
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [taxPercentage, setTaxPercentage] = useState(0);
  const [createNoteNumber, setCreateNoteNumber] = useState("");
  const [createNoteDate, setCreateNoteDate] = useState(null);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({
    customer: "",
    creditNoteNumber: "",
    creditNoteDate: "",
    salesperson: "",
    subject: "",
    items: [
      {
        item: "",
        description: "",
        quantity: "",
        rate: "",
      },
    ],
    customerNotes: "",
    termsAndConditions: "",
    discount: "",
    tax: "",
  });

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        name: "",
        itemId: "",
        account: "",
        quantity: "",
        rate: "",
        amount: "",
      },
    ]);

    setErrors((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          item: "",
          description: "",
          quantity: "",
          rate: "",
        },
      ],
    }));
  };

  const handleRemoveItem = (index) => {
    if (items.length > 1) {
      const updatedItems = [...items];
      updatedItems.splice(index, 1);
      setItems(updatedItems);

      const updatedErrors = { ...errors };
      updatedErrors.items.splice(index, 1);
      setErrors(updatedErrors);
    }
  };

  const validateField = (fieldName, value, index = null) => {
    const rule = CreditNoteValidation[fieldName];

    if (!rule) return ""; // No rule defined, consider valid

    // Check if required field is empty
    if (rule.required && (!value || value.toString().trim() === "")) {
      return rule.validator.errEmpty;
    }

    // Skip validation for empty non-required fields
    if (!rule.required && (!value || value.toString().trim() === "")) {
      return "";
    }

    // Special case for date validation
    if (fieldName === "creditNoteDate" && value) {
      const today = dayjs().startOf("day");
      const selectedDate = dayjs(value).startOf("day");
      if (selectedDate.isAfter(today)) {
        return rule.validator.error;
      }
      return "";
    }

    // Validate against regex pattern
    if (value && !rule.validator.regEx.test(value.toString())) {
      return rule.validator.error;
    }

    return ""; // No error
  };

  const handleFormChange = (field, value) => {
    const error = validateField(field, value);

    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));

    // Update the corresponding state
    switch (field) {
      case "creditNoteNumber":
        setCreateNoteNumber(value);
        break;
      case "subject":
        setSubject(value);
        break;
      case "salesperson":
        setSalesperson(value);
        break;
      case "customerNotes":
        setCustomerNotes(value);
        break;
      case "discount":
        setDiscountPercentage(Number(value) || 0);
        break;
      case "tax":
        setTaxPercentage(Number(value) || 0);
        break;
      default:
        break;
    }
  };

  const handleCustomerChange = (event, newValue) => {
    setCustomer(newValue);
    setCustomerId(newValue?._id || "");

    const error = validateField("customer", newValue?.name || "");
    setErrors((prev) => ({
      ...prev,
      customer: error,
    }));
  };

  // Add date validation handler
  const handleDateChange = (date) => {
    if (date) {
      const utcDateString = dayjs.utc(date.format("YYYY-MM-DD")).toISOString();
      setCreateNoteDate(utcDateString);

      const error = validateField("creditNoteDate", utcDateString);
      setErrors((prev) => ({
        ...prev,
        creditNoteDate: error,
      }));
    } else {
      setCreateNoteDate(null);
      setErrors((prev) => ({
        ...prev,
        creditNoteDate: "Credit Note date is required",
      }));
    }
  };

  const handleItemChange = (value, index, key) => {
    const updatedItems = [...items];
    const updatedErrors = { ...errors };

    if ((key === "quantity" || key === "rate") && Number(value) < 0) {
      return;
    }
    if (key === "invoiceQuantity" || key === "unit") {
      const qty =
        key === "invoiceQuantity"
          ? Number(value)
          : Number(updatedItems[index].quantity);
      const unitCost =
        key === "unit" ? Number(value) : Number(updatedItems[index].rate);
      updatedItems[index]["amount"] = qty * unitCost;
      calculateSubTotal();
    }

    if (key === "name") {
      const selectedItem = itemList?.find((item) => item.title === value);
      if (selectedItem) {
        updatedItems[index].itemId = selectedItem._id || "";
        updatedItems[index].rate = Number(selectedItem.sellingPrice || 0);
        updatedItems[index].account =
          selectedItem.desc ||
          selectedItem.description ||
          selectedItem.account ||
          "";
        updatedItems[index].amount =
          selectedItem.sellingPrice * selectedItem.quantity;
        calculateSubTotal();
      }
    }

    if (key === "quantity" || key === "rate") {
      updatedItems[index][key] = Number(value);
    } else {
      updatedItems[index][key] = value;
    }

    // Validate the changed field
    let error = "";
    if (key === "name") {
      error = validateField("item", value);
      updatedErrors.items[index] = {
        ...updatedErrors.items[index],
        item: error,
      };
    } else if (key === "account") {
      error = validateField("account", value);
      updatedErrors.items[index] = {
        ...updatedErrors.items[index],
        account: error,
      };
    } else if (key === "quantity") {
      error = validateField("quantity", value);
      updatedErrors.items[index] = {
        ...updatedErrors.items[index],
        quantity: error,
      };
    } else if (key === "rate") {
      error = validateField("rate", value);
      updatedErrors.items[index] = {
        ...updatedErrors.items[index],
        rate: error,
      };
    }

    // Recalculate amount
    if (key === "quantity" || key === "rate") {
      const qty = Number(updatedItems[index].quantity) || 0;
      const rate = Number(updatedItems[index].rate) || 0;
      updatedItems[index].amount = qty * rate;
    }

    setItems(updatedItems);
    setErrors(updatedErrors);
  };

  const calculateSubTotal = () => {
    const total = items.reduce((sum, item) => {
      const amount =
        (Number(item.invoiceQuantity) || 0) * (Number(item.rate) || 0);
      return sum + amount;
    }, 0);
    return Number(total.toFixed(2)) || 0;
  };

  const calculateDiscountValue = () => {
    const subTotal = calculateSubTotal();
    const discount = (subTotal * (Number(discountPercentage) || 0)) / 100;
    return Number(discount.toFixed(2)) || 0;
  };

  const calculateTaxValue = () => {
    const subTotal = calculateSubTotal();
    const discountedAmount = subTotal - calculateDiscountValue();
    const tax = (discountedAmount * (Number(taxPercentage) || 0)) / 100;
    return Number(tax.toFixed(2)) || 0;
  };

  const calculateGrandTotal = () => {
    const subTotal = calculateSubTotal();
    const discountValue = calculateDiscountValue();
    const taxValue = calculateTaxValue();
    const total = subTotal - discountValue + taxValue;
    return Number(total.toFixed(2)) || 0;
  };

  const CustomDropdownIcon = () => (
    <img
      src={dropdown_black}
      alt="dropdown"
      height={22}
      width={22}
      style={{ paddingRight: "10px" }}
    />
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate all fields before submission
    const newErrors = { ...errors };

    // Validate customer
    newErrors.customer = validateField("customer", customer?.name || "");

    // Validate credit note number
    newErrors.creditNoteNumber = validateField(
      "creditNoteNumber",
      createNoteNumber
    );

    // Validate credit note date
    newErrors.creditNoteDate = validateField("creditNoteDate", createNoteDate);

    // Validate items
    items.forEach((item, index) => {
      newErrors.items[index] = {
        item: validateField("item", item.name),
        quantity: validateField("quantity", item.quantity),
        rate: validateField("rate", item.rate),
      };
    });

    setErrors(newErrors);

    const subTotal = calculateSubTotal();
    const discountValue = calculateDiscountValue();
    const taxValue = calculateTaxValue();
    const grandTotal = calculateGrandTotal();

    const payload = {
      customer: customerId,
      creditNoteNumber: createNoteNumber,
      creditNoteDate: createNoteDate,
      salesperson: salesperson,
      subject: subject,
      customerNotes: customerNotes,
      itemInformation: items.map((item) => ({
        itemId: item.itemId,
        name: item.name,
        account: item.account || "Discount", // Default to "Discount" if not specified
        quantity: item.invoiceQuantity,
        rate: item.rate,
        amount: item.amount,
      })),
      total: subTotal,
      discountPercentage: discountPercentage,
      discountValue: discountValue,
      taxPercentage: taxPercentage,
      taxValue: taxValue,
      grandTotal: grandTotal,
    };

    createNotes(payload);
  };

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      "&.Mui-error fieldset": {
        borderColor: "red",
        borderWidth: "2px",
      },
    },
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Box px="20px" mt={2}>
          <Box sx={styles.header}>
            <Typography sx={styles.title}>Create Credit Notes</Typography>
          </Box>
        </Box>
        <Box sx={styles.container}>
          {/* Form Section */}
          <Box sx={styles.formSection}>
            <Grid container spacing={2}>
              {/* First Row */}
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Customer Name</Typography>
                  <Autocomplete
                    id="customer-select"
                    options={[
                      { _id: "add_new", name: "+ New Customer" },
                      ...(customerList || []),
                    ]}
                    getOptionLabel={(option) => option?.name || ""}
                    value={customer}
                    onChange={(_, newValue) => {
                      if (newValue?._id === "add_new") {
                        navigate("/dashboard/addNewCustomer"); // redirect page
                        return;
                      }
                      handleCustomerChange(_, newValue);
                    }}
                    renderOption={(props, option) => (
                      <li
                        {...props}
                        style={
                          option._id === "add_new"
                            ? { color: color.secondary }
                            : {}
                        }
                      >
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Customer"
                        size="small"
                        sx={{ ...styles.textField, ...textFieldStyles }}
                        error={!!errors.customer}
                        helperText={errors.customer}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Credit Note#</Typography>
                  <TextField
                    fullWidth
                    placeholder="Credit Note"
                    size="small"
                    sx={{ ...styles.textField, ...textFieldStyles }}
                    onChange={(e) =>
                      handleFormChange("creditNoteNumber", e.target.value)
                    }
                    error={!!errors.creditNoteNumber}
                    helperText={errors.creditNoteNumber}
                  />
                </Box>
              </Grid>

              {/* Second Row */}

              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Credit Note Date</Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box
                      sx={{
                        ...styles.datePickerOuter,
                        borderRadius: "8px !important",
                        position: "relative",
                      }}
                    >
                      <DatePicker
                        value={createNoteDate ? dayjs(createNoteDate) : null}
                        onChange={handleDateChange}
                        inputFormat="DD/MM/YYYY"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            fullWidth
                            error={!!errors.creditNoteDate}
                            helperText={errors.creditNoteDate}
                            sx={{
                              ...styles.datePicker,
                              ...textFieldStyles,
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px !important",
                                overflow: "hidden",
                              },
                              "& .MuiInputAdornment-root": {
                                paddingLeft: "5px",
                              },
                            }}
                          />
                        )}
                      />
                      {errors.creditNoteDate && (
                        <Typography
                          sx={{
                            color: "red !important",
                            fontSize: "12px",
                            mt: 0.5,
                            position: "absolute",
                            bottom: "-20px",
                            left: "0",
                          }}
                        >
                          {errors.creditNoteDate}
                        </Typography>
                      )}
                    </Box>
                  </LocalizationProvider>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <FormControl
                    fullWidth
                    sx={{
                      ...textFieldStyles,
                      "& .MuiOutlinedInput-root": {
                        height: "2.5em",
                        borderRadius: "10px",
                      },
                    }}
                  >
                    <Typography sx={styles.label}>Salesperson</Typography>
                    <Select
                      value={salesperson}
                      onChange={(e) => {
                        setSalesperson(e.target.value);
                      }}
                      fullWidth
                      displayEmpty
                      error={!!errors.salesperson}
                      helperText={errors.salesperson}
                      renderValue={(selected) =>
                        selected ? (
                          selected
                        ) : (
                          <span style={{ color: "#999" }}>
                            Select Salesperson
                          </span>
                        )
                      }
                    >
                      <MenuItem value="John Doe">John Doe</MenuItem>
                      <MenuItem value="John Smith">John Smith</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Grid>

              {/* Third Row */}
              <Grid item xs={12}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Subject</Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter Subject"
                    size="small"
                    onChange={(e) => setSubject(e.target.value)}
                    error={!!errors.subject}
                    helperText={errors.subject}
                    sx={{
                      ...styles.multilineTextField,
                      ...textFieldStyles,
                      "& .MuiOutlinedInput-root": {
                        height: "76px",
                        borderRadius: "8px !important",
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={styles.field}>
                  <Typography>Customer Notes</Typography>
                  <TextField
                    rows={2}
                    fullWidth
                    error={!!errors.customerNotes}
                    helperText={errors.customerNotes}
                    placeholder="Enter Customer Note"
                    sx={{
                      ...styles.multilineTextField,
                      ...textFieldStyles,
                      marginTop: "8px !important",
                      "& .MuiOutlinedInput-root": {
                        height: "76px",
                        borderRadius: "8px",
                        "& fieldset": {
                          border: "1px solid #D9D9D9",
                        },
                        "&:hover fieldset": {
                          borderColor: "#28AA4A",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#28AA4A",
                        },
                      },
                    }}
                    onChange={(e) => setCustomerNotes(e.target.value)}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
        {/* Items Section */}
        <Box>
          <Typography sx={{ ...styles.itemsTitle }}>
            Items & Services
          </Typography>
          <Box sx={styles.container}>
            <Box sx={styles.itemsSection}>
              {items.map((item, index) => {
                const stockQuantity =
                  itemList.find((p) => p._id === item.itemId)?.quantity || 0;
                return (
                  <Box key={index} sx={styles.itemRow}>
                    <Box sx={{ width: "22%", position: "relative" }}>
                      <Autocomplete
                        id={`item-select-${index}`}
                        options={[
                          { _id: "add_new", title: "+ New Item" },
                          ...(itemList || []),
                        ]}
                        getOptionLabel={(option) => option?.title || ""}
                        value={
                          itemList?.find((i) => i.title === item.name) || null
                        }
                        onChange={(_, newVal) => {
                          if (newVal?._id === "add_new") {
                            navigate("/dashboard/addNewItem"); // redirect page
                            return;
                          }
                          if (!newVal) {
                            handleItemChange("", index, "name");
                            handleItemChange("", index, "invoiceQuantity");
                            return;
                          }
                          handleItemChange(newVal?.title || "", index, "name");
                        }}
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            style={
                              option._id === "add_new"
                                ? { color: color.secondary }
                                : {}
                            }
                          >
                            {option.title}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Item"
                            size="small"
                            sx={{ ...textFieldStyles, ...styles.textField }}
                            error={!!errors.items[index]?.item}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loading ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                      {errors.items[index]?.item && (
                        <Typography
                          sx={{ color: "red", fontSize: "12px", mt: 0.5 }}
                        >
                          {errors.items[index]?.item}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ width: "22%", position: "relative" }}>
                      <FormControl
                        fullWidth
                        sx={{
                          ...textFieldStyles,
                          "& .MuiOutlinedInput-root": {
                            height: "41px",
                            borderRadius: "10px",
                          },
                        }}
                      >
                        <Select
                          onChange={(e) =>
                            handleItemChange(e.target.value, index, "account")
                          }
                          name="account"
                          value={item.account || ""}
                          displayEmpty
                          renderValue={(selected) =>
                            selected ? (
                              selected
                            ) : (
                              <span style={{ color: "#999" }}>
                                Select Account
                              </span>
                            )
                          }
                        >
                          {accountOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {errors.items[index]?.account && (
                        <Typography
                          sx={{ color: "red", fontSize: "12px", mt: 0.5 }}
                        >
                          {errors.items[index]?.account}
                        </Typography>
                      )}
                    </Box>

                    <Grid item xs={12} lg={6}>
                      <Box
                        display="flex"
                        alignItems="center"
                        sx={{
                          ...styles.customInput,
                          width: "100%",
                          position: "relative",
                        }}
                      >
                        <TextField
                          value={item.invoiceQuantity}
                          type="number"
                          placeholder="Quantity"
                          error={!!errors.items[index]?.quantity}
                          sx={{ ...styles.quality, ...textFieldStyles }}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            const maxQuantity = stockQuantity;
                            if (
                              !isNaN(value) &&
                              value >= 0 &&
                              value <= maxQuantity
                            ) {
                              handleItemChange(value, index, "invoiceQuantity");
                            } else if (e.target.value === "") {
                              handleItemChange("", index, "invoiceQuantity");
                            } else {
                              const validValue = value < 0 ? 0 : maxQuantity;
                              handleItemChange(
                                validValue,
                                index,
                                "invoiceQuantity"
                              );
                            }
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" variant="filled">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    bgcolor: "#DEDEDE",
                                    height: 41,
                                    p: 0,
                                    ...styles.inputBgBox,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      minWidth: 21,
                                      height: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "end",
                                      fontWeight: "600",
                                      userSelect: "none",
                                      borderRadius: "8px",
                                      px: 0.5,
                                    }}
                                  >
                                    {stockQuantity}
                                  </Box>
                                  <Tooltip
                                    arrow
                                    placement="top"
                                    title={
                                      <Box
                                        sx={{
                                          bgcolor: "white",
                                          p: 1,
                                          borderRadius: "6px",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "10px",
                                            fontWeight: 600,
                                            color: "#373D3F",
                                            mb: 1,
                                          }}
                                        >
                                          Stock Details
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: "10px",
                                            color: "#373D3F",
                                            fontWeight: 400,
                                          }}
                                        >
                                          Stock on Hand : {stockQuantity || "0"}
                                        </Typography>
                                      </Box>
                                    }
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: "white !important",
                                          p: 0,
                                          border:
                                            "1px solid #878787 !important",
                                          boxShadow: "none !important",
                                          "& .MuiTooltip-arrow": {
                                            color: "white !important",
                                            "&::before": {
                                              border: "1px solid #878787",
                                              backgroundColor:
                                                "white !important",
                                            },
                                          },
                                        },
                                      },
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      edge="end"
                                      sx={{
                                        color: "#878787",
                                      }}
                                    >
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                        />
                        {errors.items[index]?.quantity && (
                          <Typography
                            sx={{
                              color: "red",
                              fontSize: "12px",
                              mt: 0.5,
                              position: "absolute",
                              bottom: "-20px",
                            }}
                          >
                            {errors.items[index]?.quantity}
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    <Box sx={{ position: "relative" }}>
                      <TextField
                        size="small"
                        type="number"
                        placeholder="Rate"
                        value={
                          items[index]?.rate === ""
                            ? ""
                            : String(items[index]?.rate)
                        }
                        onChange={(e) =>
                          handleItemChange(e.target.value, index, "rate")
                        }
                        error={!!errors.items[index]?.rate}
                        sx={{ ...styles.rateField, ...textFieldStyles }}
                      />
                      {errors.items[index]?.rate && (
                        <Typography
                          sx={{
                            color: "red",
                            fontSize: "12px",
                            mt: 0.5,
                            position: "absolute",
                            bottom: "-20px",
                            width: "100%",
                          }}
                        >
                          {errors.items[index]?.rate}
                        </Typography>
                      )}
                    </Box>

                    <TextField
                      size="small"
                      placeholder="Amount"
                      value={
                        (items[index]?.rate || 0) *
                        (items[index]?.invoiceQuantity || 0) || ""
                      }
                      InputProps={{
                        readOnly: true,
                        sx: {
                          paddingLeft: "6px", // Adjust as needed
                          "& input": {
                            padding: "10px 6px",
                            textAlign: "left",
                            marginLeft: "1px", // Adjust this value to control spacing between $ and amount
                          },
                        },
                      }}
                      sx={{
                        width: "200px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          backgroundColor: "#fff",
                          height: "41px",
                          "& fieldset": {
                            border: "1px solid #D9D9D9",
                          },
                        },
                      }}
                    />

                    {index === 0 ? (
                      <IconButton onClick={handleAddItem} sx={styles.addButton}>
                        <AddIcon />
                      </IconButton>
                    ) : (
                      <IconButton
                        onClick={() => handleRemoveItem(index)}
                        sx={styles.deleteButton}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* Summary Section */}
        <Box>
          <Typography sx={{ ...styles.itemsTitle, marginBottom: "20px" }}>
            Discount and Tax (%)
          </Typography>
          <Grid container spacing={0}>
            <Grid item xs={12} md={7}>
              <Box sx={styles.container4}>
                <Box
                  sx={{
                    height: "140px",
                    backgroundColor: "white",
                    borderRadius: "8px",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          sx={{ fontSize: "14px", color: "#202020", mb: 0.5 }}
                        >
                          Discount
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          placeholder="Enter Discount"
                          value={discountPercentage || ""}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            if (value >= 0 && value <= 100) {
                              setDiscountPercentage(value);
                            } else if (e.target.value === "") {
                              setDiscountPercentage(0);
                            }
                          }}
                          inputProps={{ min: 0, max: 100 }}
                          sx={{
                            backgroundColor: "white",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px !important",
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          sx={{ fontSize: "14px", color: "#202020", mb: 0.5 }}
                        >
                          Tax
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          placeholder="Enter Tax"
                          value={taxPercentage || ""}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            if (value >= 0 && value <= 100) {
                              setTaxPercentage(value);
                            } else if (e.target.value === "") {
                              setTaxPercentage(0);
                            }
                          }}
                          inputProps={{ min: 0, max: 100 }}
                          sx={{
                            backgroundColor: "white",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px !important",
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={styles.container3}>
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    height: "140px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ color: "#666" }}>Total</Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      ${calculateSubTotal().toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ color: "#666" }}>
                      Tax ({taxPercentage || 0}%)
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      ${calculateTaxValue().toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ color: "#666" }}>
                      Discount ({discountPercentage || 0}%)
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      -${calculateDiscountValue().toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      pt: 1,
                      borderTop: "1px solid #eee",
                    }}
                  >
                    <Typography sx={{ fontWeight: 500 }}>
                      Grand Total
                    </Typography>
                    <Typography sx={{ fontWeight: 500, color: "#28AA4A" }}>
                      ${calculateGrandTotal().toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ ...styles.container }}>
          <Box sx={styles.actionButtons}>
            <Button
              type="submit"
              variant="contained"
              sx={styles.draftButton}
              disabled={
                !customerId ||
                !subject ||
                !salesperson ||
                !createNoteDate ||
                !createNoteNumber ||
                !customerNotes
              }
            >
              Save as Draft
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={styles.saveButton}
              disabled={
                !customerId ||
                !subject ||
                !salesperson ||
                !createNoteDate ||
                !createNoteNumber ||
                !customerNotes
              }
            >
              Save
            </Button>
            <Button variant="outlined" sx={styles.cancelButton}>
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
};

export default CreateNotesPage;
